import {ActionContext} from "vuex";
import {IAppState} from "@/interfaces/app-state.interface";
import {Vue} from "vue-property-decorator";
import {IBook} from "@/interfaces/book.interface";
import {bookResources} from "@/resources/book.resources";
import {IApp} from "@/interfaces/app.interface";
import {cookieBannerHelper} from "@/shared/CookieBanner/helpers/cookie-banner-helper";

type BookActionContext = ActionContext<IBook.IState, IAppState>

export const BookStore: BookActionContext = {
  // @ts-ignore
  namespaced: true,

  state: {
    loading: false,
    books: {
      [IBook.BookName.Corps]: [],
      [IBook.BookName.tarifs]: [],
      [IBook.BookName.CarStatuses]: [],
      [IBook.BookName.balanceTypes]: [],
      [IBook.BookName.parks]: [],
      [IBook.BookName.globalParks]: [],
      [IBook.BookName.parksMyAll]: [],
      [IBook.BookName.claimTypes]: [],
      [IBook.BookName.parksOwners]: [],
      [IBook.BookName.carBalanceTypes]: [],
      [IBook.BookName.carBalanceTypes]: [],
      [IBook.BookName.carsMyCorpForFavorite]: [],
      [IBook.BookName.demoCars]: [],
      [IBook.BookName.carTypes]: [],
      [IBook.BookName.groupTypes]: [],
      [IBook.BookName.cameras]: [],
      [IBook.BookName.visitPurposeTypes]: [],
      [IBook.BookName.scenarioTypes]: [],
      [IBook.BookName.corpsParents]: [],
      [IBook.BookName.dashboardTSumTypes]: [],
    },
  },

  getters: {
    parksBook: (state: IBook.IState): IBook.ParkBookItem[] => {
      let storageValue = localStorage.getItem(IApp.LocalStorageKeys.SelectParksGrouping);
      const groupingParks = storageValue && cookieBannerHelper.cookieConfig.optional ? JSON.parse(storageValue) : true;
      let usedBook = groupingParks ? IBook.BookName.globalParks : IBook.BookName.parks;
      return state.books[usedBook];
    },
  },

  mutations: {
    setProperty(state: IBook.IState, { name, value }: { name: string, value: any}) { Vue.set(state, name, value); },
    startLoading(state: IBook.IState) { state.loading = true },
    stopLoading(state: IBook.IState) { state.loading = false },
    setBook(state: IBook.IState, { name, value }: { name: string, value: any}) { Vue.set(state.books, name, value);},
    addItemInBook(state: IBook.IState, payload: { bookName: IBook.BookName, item: IBook.CatalogData }) {
      Vue.set(state.books[payload.bookName], state.books[payload.bookName].length, payload.item);
      },
  },

  actions: {
    async initBooksForPage({ state, rootState, commit, dispatch }: BookActionContext, pageName: IApp.ROUTE_NAME) {
      const params: any = {};
      const listBooks = [
        IBook.TableName.BalanceTypes,
        IBook.TableName.Corps,
        IBook.TableName.Tarifs,
        IBook.TableName.parks,
        IBook.TableName.globalParks,
        IBook.TableName.carTypes,
      ];

      switch (pageName) {
        case IApp.ROUTE_NAME.HOME:
          listBooks.push(...[
            IBook.TableName.carsMyCorpForFavorite,
          ]);
          break;

        case IApp.ROUTE_NAME.PAGE_CAR_LIST:
          listBooks.push(...[
            IBook.TableName.newOffer,
            IBook.TableName.carsHistory,
            IBook.TableName.CarStatuses,
          ]);
          break;

        case IApp.ROUTE_NAME.PAGE_BARRIERS:
        case IApp.ROUTE_NAME.PAGE_SCREEN_PAY:
          listBooks.push(...[
            IBook.TableName.claimTypes,
            IBook.TableName.visitPurposeTypes,
          ]);
          break;

        case IApp.ROUTE_NAME.PAGE_PROFILE:
          listBooks.push(...[
            IBook.TableName.parksMyAll,
          ]);
          break;

        case IApp.ROUTE_NAME.PAGE_CORPS:
          listBooks.push(...[
            IBook.TableName.corpsParents,
          ]);
          break;

        case IApp.ROUTE_NAME.PAGE_DASHBOARD:
          listBooks.push(...[
            IBook.TableName.parksOwners,
            IBook.TableName.dashboardTSumTypes,
          ]);
          break;

        case IApp.ROUTE_NAME.PAGE_PARKING_ACCESS:
          listBooks.push(...[
            IBook.TableName.Corps,
          ]);
          break;

        case IApp.ROUTE_NAME.PAGE_REPORT_MOVEMENTS:
        case IApp.ROUTE_NAME.PAGE_REPORT_BALANCE:
        case IApp.ROUTE_NAME.PAGE_REPORT_EVENTS:
        case IApp.ROUTE_NAME.PAGE_REPORT_CARS:
        case IApp.ROUTE_NAME.PAGE_REPORT_PARKS:
        case IApp.ROUTE_NAME.PAGE_REPORT_AGENT:
        case IApp.ROUTE_NAME.PAGE_REPORT_CARS_BALANCE:
          listBooks.push(...[
            IBook.TableName.CarStatuses,
            IBook.TableName.carBalanceTypes,
            IBook.TableName.groupTypes,
            IBook.TableName.cameras,
            IBook.TableName.scenarioTypes,
            IBook.TableName.visitPurposeTypes,
          ]);
          params.reportName = rootState.route.meta.command;
          break;

        default:
          break;
      }

      commit('startLoading');
      try {
        await dispatch('getBook', { bookNames: listBooks, ...params });
      }
      catch (error) { await dispatch('handleError', error, { root: true }); }
      commit('stopLoading');
    },

    async getBook({ state, commit, rootState }: BookActionContext, params: IBook.GetBookParams) {
      let booksForCheck = params.bookNames ? params.bookNames : params.tableName ? [params.tableName] : [];
      let booksForGet = [];

      booksForCheck.forEach(book => {
        let emptyBook = !state.books[book] || (state.books[book] && !state.books[book].length);

        if (emptyBook || params.needUpdate) {
          booksForGet.push(book);
        }
      });

      let paramsToSend = {
        ...params,
        bookNames: booksForGet,
      };
      let paramsForDelete = ['needUpdate', 'tableName',];
      paramsForDelete.forEach(param => delete paramsToSend[param]);

      if (booksForGet.length) {
        let response = await bookResources.getBook(paramsToSend);
        let bookNames = Object.keys(response.data.result);
        bookNames.forEach(book => {
          commit('setBook', { name: book, value: response.data.result[book] });
        });
      }
    },

    async getOneBook({ state, rootState, commit, dispatch }: BookActionContext, { tableName, payload }: { tableName: IBook.TableName, payload?: any }) {
      commit('startLoading');
      try {
        await dispatch('getBook', { tableName, payload });
      }
      catch (error) { dispatch('handleError', error) }
      commit('stopLoading');
    },
  }
};
